<template>
  <!-- Header -->
  <div class="header">
    <!-- Logo -->
    <div class="header-left active" style="justify-content: center">
      <router-link to="/dashboard" class="logo logo-normal">
        <img :src="logoUrl" :alt="appName" @error="useDefaultLogo('normal')" />
      </router-link>
      <router-link to="/dashboard" class="logo logo-white">
        <img :src="logoWhiteUrl" :alt="appName" @error="useDefaultLogo('white')" />
      </router-link>
      <router-link to="/dashboard" class="logo-small">
        <img :src="logoSmallUrl" :alt="appName" @error="useDefaultLogo('small')" />
      </router-link>
      <a id="toggle_btn" href="javascript:void(0);" @click="toggleSidebar">
        <vue-feather :type="isRtl ? 'chevrons-right' : 'chevrons-left'"></vue-feather>
      </a>
    </div>
    <!-- /Logo -->

    <a id="mobile_btn" class="mobile_btn" href="javascript:void(0);" @click="toggleSidebar1">
      <vue-feather type="menu" size="23"></vue-feather>
    </a>

    <div ref="navSearch" class="nav-global-search" :class="{ open: navSearchOpen }">
      <button type="button" class="responsive-search nav-search-toggle" :aria-label="$t('navSearch.placeholder')" @click="openNavSearch">
        <i class="fa fa-search" aria-hidden="true"></i>
      </button>
      <div class="nav-search-control">
        <vue-feather class="nav-search-icon" type="search" size="17"></vue-feather>
        <input ref="navSearchInput" v-model="navSearchQuery" type="search" autocomplete="off"
          :placeholder="$t('navSearch.placeholder')" @focus="navSearchOpen = true"
          @keydown.down.prevent="moveNavSearch(1)" @keydown.up.prevent="moveNavSearch(-1)"
          @keydown.enter.prevent="openActiveSearchResult" @keydown.esc.prevent="closeNavSearch" />
        <button v-if="navSearchQuery" type="button" class="nav-search-clear" :aria-label="$t('navSearch.clear')" @click="clearNavSearch">
          <vue-feather type="x" size="15"></vue-feather>
        </button>
        <kbd class="nav-search-shortcut">⌘ K</kbd>
      </div>
      <transition name="nav-search-fade">
        <div v-if="navSearchOpen" class="nav-search-results">
          <div class="nav-search-results-title">{{ navSearchQuery ? $t('navSearch.results') : $t('navSearch.quickAccess') }}</div>
          <button v-for="(item, index) in filteredNavPages" :key="`${item.path}-${item.label}-${index}`" type="button"
            class="nav-search-result" :class="{ active: navSearchActiveIndex === index }"
            @mouseenter="navSearchActiveIndex = index" @click="navigateFromSearch(item)">
            <span class="nav-search-result-icon"><vue-feather :type="item.icon" size="17"></vue-feather></span>
            <span class="nav-search-result-copy"><strong>{{ item.label }}</strong><small>{{ item.group }}</small></span>
            <vue-feather :type="isRtl ? 'arrow-left' : 'arrow-right'" size="15"></vue-feather>
          </button>
          <div v-if="navEntityLoading" class="nav-search-loading">
            <span class="spinner-border spinner-border-sm"></span><span>{{ $t('navSearch.searching') }}</span>
          </div>
          <div v-else-if="!filteredNavPages.length" class="nav-search-empty">
            <vue-feather type="search" size="22"></vue-feather><span>{{ $t('navSearch.noResults') }}</span>
          </div>
        </div>
      </transition>
    </div>

    <!-- Header Menu -->
    <ul class="nav user-menu d-flex justify-content-end">
      <li class="nav-item dropdown nav-item-box language-menu">
        <a href="javascript:void(0);" class="dropdown-toggle nav-link language-trigger" data-bs-toggle="dropdown">
          <vue-feather type="globe"></vue-feather>
          <span>{{ currentLocale.toUpperCase() }}</span>
        </a>
        <div class="dropdown-menu language-dropdown dropdown-menu-end">
          <button v-for="language in languages" :key="language.code" type="button" class="dropdown-item"
            :class="{ active: currentLocale === language.code }" @click="changeLanguage(language.code)">
            <span class="language-code">{{ language.short }}</span>
            <span>{{ $t(`language.${language.code}`) }}</span>
            <vue-feather v-if="currentLocale === language.code" type="check" size="15"></vue-feather>
          </button>
        </div>
      </li>
      <li class="nav-item nav-item-box">
        <a href="javascript:void(0);" id="btnFullscreen" @click="initFullScreen">
          <vue-feather type="maximize"></vue-feather>
        </a>
      </li>
      <!-- Notifications -->
      <li class="nav-item dropdown nav-item-box">
        <a
          href="javascript:void(0);"
          class="dropdown-toggle nav-link"
          data-bs-toggle="dropdown"
          @click="loadNotifications"
        >
          <vue-feather type="bell"></vue-feather>
          <span v-if="notificationUnreadCount" class="badge rounded-pill">
            {{ notificationUnreadCount > 99 ? "99+" : notificationUnreadCount }}
          </span>
        </a>
        <div class="dropdown-menu notifications">
          <div class="topnav-dropdown-header">
            <div>
              <span class="notification-title">{{ $t('header.notifications') }}</span>
              <span v-if="notificationUnreadCount" class="notification-count">
                {{ notificationUnreadCount }} non lue{{ notificationUnreadCount > 1 ? 's' : '' }}
              </span>
            </div>
            <button v-if="notifications.length" type="button" class="notification-clear-btn"
              @click.prevent="clearNotifications" title="Effacer toutes les notifications">
              <vue-feather type="x" size="16"></vue-feather>
            </button>
          </div>
          <div class="noti-content">
            <ul class="notification-list">
              <li v-if="notificationsLoading" class="notification-message">
                <div class="notification-empty">
                  <span class="spinner-border spinner-border-sm" role="status"></span>
                  <span>{{ $t('header.loading') }}</span>
                </div>
              </li>
              <li v-else-if="!notifications.length" class="notification-message text-center">
                <div class="notification-empty notification-empty-state">
                  <span class="notification-empty-icon"><vue-feather type="bell-off"></vue-feather></span>
                  <strong>{{ $t('header.upToDate') }}</strong>
                  <span>{{ $t('header.noNotifications') }}</span>
                </div>
              </li>
              <template v-else>
                <li
                  v-for="notification in notifications"
                  :key="notification.id"
                  class="notification-message"
                  :class="{ unread: !notification.is_read }"
                >
                  <router-link :to="notification.link || '/dashboard'" @click="openNotification(notification)">
                    <span v-if="!notification.is_read" class="notification-unread-dot"></span>
                    <div class="media d-flex">
                      <span
                        class="notification-icon flex-shrink-0"
                        :class="notificationSeverityClass(notification.severity)"
                      >
                        <vue-feather :type="notificationIcon(notification.type)"></vue-feather>
                      </span>
                      <div class="media-body flex-grow-1">
                        <p class="noti-details"><span class="noti-title">{{ notification.title }}</span></p>
                        <p class="notification-description">{{ notification.message }}</p>
                        <p class="noti-time">
                          <span class="notification-time">{{ formatNotificationTime(notification.created_at) }}</span>
                        </p>
                      </div>
                    </div>
                  </router-link>
                </li>
              </template>
            </ul>
          </div>
          <div class="topnav-dropdown-footer">
            <button type="button" :disabled="!notificationUnreadCount" @click.prevent="markNotificationsRead">
              <vue-feather type="check-circle" size="16"></vue-feather>
              {{ $t('header.markRead') }}
            </button>
          </div>
        </div>
      </li>
      <!-- /Notifications -->
      <li class="nav-item dropdown has-arrow main-drop">
        <a href="javascript:void(0);" class="dropdown-toggle nav-link userset" data-bs-toggle="dropdown">
          <span class="user-info">
            <span class="user-letter">
              <img :src="userProfileImage || defaultAvatarUrl" alt="" class="img-fluid user-avatar-img" />
            </span>
            <span class="user-detail">
              <span class="user-name">{{
                currentUser?.nom_utilisateur || "User"
                }}</span>
              <span class="user-role">{{ currentUser?.role || "" }}</span>
            </span>
          </span>
        </a>
        <div class="dropdown-menu menu-drop-user">
          <div class="profilename">
            <div class="profileset">
              <span class="user-img">
                <img :src="userProfileImage || defaultAvatarUrl" alt="" class="user-avatar-img" />
                <span class="status online"></span>
              </span>
              <div class="profilesets">
                <h6>{{ currentUser?.nom_utilisateur || "User" }}</h6>
                <h5>{{ currentUser?.role || "" }}</h5>
              </div>
            </div>
            <hr class="m-0" />
            <router-link class="dropdown-item" to="/profile">
              <vue-feather class="me-2" type="user"></vue-feather>{{ $t('header.profile') }}</router-link>
            <router-link v-if="canAccessCompanySettings" class="dropdown-item" to="/societe"><vue-feather class="me-2"
                type="settings"></vue-feather>{{ $t('header.settings') }}</router-link>
            <hr class="m-0" />
            <a class="dropdown-item logout pb-0" href="#" @click.prevent="logoutUser">
              <img :src="logoutIconUrl" class="me-2" alt="img" />{{ $t('header.logout') }}
            </a>
          </div>
        </div>
      </li>
    </ul>
    <!-- /Header Menu -->

    <!-- Mobile Menu -->
    <div class="dropdown mobile-user-menu">
      <a href="javascript:void(0);" class="nav-link dropdown-toggle mobile-more-button" data-bs-toggle="dropdown"
        aria-expanded="false" aria-label="Menu">
        <vue-feather type="more-vertical" size="19"></vue-feather>
      </a>
      <div class="dropdown-menu dropdown-menu-end mobile-quick-menu">
        <div class="mobile-quick-profile">
          <img :src="userProfileImage || defaultAvatarUrl" alt="" class="user-avatar-img" />
          <span><strong>{{ currentUser?.nom_utilisateur || "User" }}</strong><small>{{ currentUser?.role || "" }}</small></span>
        </div>
        <div class="mobile-language-list">
          <button v-for="language in languages" :key="`mobile-${language.code}`" type="button"
            :class="{ active: currentLocale === language.code }" @click="changeLanguage(language.code)">
            {{ language.short }}
          </button>
        </div>
        <router-link class="dropdown-item" to="/profile"><vue-feather type="user" size="16" />{{ $t('header.profile') }}</router-link>
        <router-link v-if="canAccessCompanySettings" class="dropdown-item" to="/societe"><vue-feather type="settings" size="16" />{{ $t('header.settings') }}</router-link>
        <a class="dropdown-item mobile-logout" href="#" @click.prevent="logoutUser"><vue-feather type="log-out" size="16" />{{ $t('header.logout') }}</a>
      </div>
    </div>
    <!-- /Mobile Menu -->
  </div>
  <!-- /Header -->

</template>

<script>
import { logout, getCurrentUser } from "@/services/authService";
import { getProfile } from "@/services/userService";
import { getCompanyData } from "@/services/companyService";
import {
  dismissAllNotifications,
  getNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "@/services/notificationService";
import { toast } from "vue3-toastify";
import { setAppLocale } from "@/i18n";
import { getProductsData, getServicesData } from "@/services/productsService";
import { getClientsData } from "@/services/clientsService";
import { getDevisData } from "@/services/devisService";
import { getFacturesData } from "@/services/facturesService";
import { getBonsData } from "@/services/deliveryService";

const DEFAULT_APP_NAME = "Business Manager";

export default {
  computed: {
    isRtl() {
      return this.currentLocale === "ar";
    },
    canAccessCompanySettings() {
      const role = String(this.currentUser?.role || "").toLowerCase();
      return role === "admin" || role === "dev";
    },
    availableNavPages() {
      const role = String(this.currentUser?.role || "").toLowerCase();
      const page = (key, path, icon, group, roles = []) => ({
        label: this.$t(`sidebar.${key}`), path, icon,
        group: this.$t(`sidebar.${group}`), roles,
      });
      if (role === "dev") {
        return [
          page("pendingCompanies", "/support/pending-societes", "clock", "support"),
          page("subscriptions", "/support/abonnements", "credit-card", "support"),
          page("journal", "/support/activity", "activity", "support"),
          { label: this.$t("header.profile"), path: "/profile", icon: "user", group: this.$t("header.settings"), roles: [] },
        ];
      }
      const pages = [
        page("dashboard", "/dashboard", "grid", "overview"),
        page("clients", "/tiers", "users", "relations"),
        page("quotes", "/devis", "clipboard", "sales"),
        page("invoices", "/factures", "file-text", "sales"),
        page("deliveries", "/bonsLivraison", "truck", "sales"),
        page("unitSales", "/ventes-unitaires", "shopping-bag", "sales"),
        page("payments", "/factures/paiements", "credit-card", "sales", ["admin"]),
        page("products", "/produits/list-produits", "box", "catalog"),
        page("services", "/services", "briefcase", "catalog"),
        page("stock", "/stocks", "archive", "inventory"),
        page("stockMovements", "/stock-movements", "repeat", "inventory"),
        page("warehouses", "/entrepots", "home", "configuration", ["admin"]),
        page("productCategories", "/categories", "tag", "configuration", ["admin"]),
        page("serviceCategories", "/categories-services", "layers", "configuration", ["admin"]),
        page("companyLayout", "/societe/page", "layout", "configuration", ["admin"]),
        page("employees", "/employees", "user-check", "administration", ["admin"]),
        { label: this.$t("header.settings"), path: "/societe", icon: "settings", group: this.$t("sidebar.administration"), roles: ["admin"] },
        { label: this.$t("header.profile"), path: "/profile", icon: "user", group: this.$t("header.settings"), roles: [] },
      ];
      return pages.filter((item) => !item.roles.length || item.roles.includes(role));
    },
    filteredNavPages() {
      const query = this.normalizeSearch(this.navSearchQuery);
      if (!query) return this.availableNavPages.slice(0, 7);
      const pageMatches = this.availableNavPages.filter((item) =>
        this.normalizeSearch(`${item.label} ${item.group}`).includes(query)
      );
      const entityMatches = this.navEntityItems.filter((item) => item.searchText.includes(query));
      return [...entityMatches, ...pageMatches].slice(0, 10);
    },
  },
  data() {
    const defaultLogoUrl = require("@/assets/img/logo.png");
    const defaultLogoWhiteUrl = require("@/assets/img/logo-white.png");
    const defaultLogoSmallUrl = require("@/assets/img/logo-small.png");
    const defaultFaviconUrl = require("@/assets/img/nedam_icon2.svg");

    return {
      currentUser: null,
      userProfileImage: null,
      appName: DEFAULT_APP_NAME,
      brandingCompanyName: "",
      defaultLogoUrl,
      defaultLogoWhiteUrl,
      defaultLogoSmallUrl,
      defaultFaviconUrl,
      logoUrl: defaultLogoUrl,
      logoWhiteUrl: defaultLogoWhiteUrl,
      logoSmallUrl: defaultLogoSmallUrl,
      defaultAvatarUrl: require("@/assets/img/profiles/avatar-01.jpg"),
      logoutIconUrl: require("@/assets/img/icons/log-out.svg"),
      profileImageListener: null,
      profileUpdateListener: null,
      companyBrandingListener: null,
      notifications: [],
      notificationUnreadCount: 0,
      notificationsLoading: false,
      notificationsRefreshTimer: null,
      currentLocale: ["fr", "en", "ar"].includes(document.documentElement.lang)
        ? document.documentElement.lang
        : (localStorage.getItem("app_locale") || "fr"),
      languages: [
        { code: "fr", short: "FR" },
        { code: "en", short: "EN" },
        { code: "ar", short: "AR" },
      ],
      navSearchQuery: "",
      navSearchOpen: false,
      navSearchActiveIndex: 0,
      navSearchKeyListener: null,
      navSearchOutsideListener: null,
      navSearchTimer: null,
      navEntityItems: [],
      navEntityLoading: false,
      navEntityLoadedAt: 0,
    };
  },
  async mounted() {
    // Keep the navbar indicator aligned with the locale that is actually
    // applied to the document, even after a cached page or a hard reload.
    this.currentLocale = ["fr", "en", "ar"].includes(document.documentElement.lang)
      ? document.documentElement.lang
      : (localStorage.getItem("app_locale") || "fr");
    this.initMouseoverListener();
    this.currentUser = getCurrentUser();
    await this.loadCompanyBranding();
    await this.loadUserProfileImage();
    this.profileImageListener = (event) => this.handleProfileImageUpdate(event);
    window.addEventListener("profile-image-updated", this.profileImageListener);
    this.profileUpdateListener = () => this.handleProfileUpdate();
    window.addEventListener("profile-updated", this.profileUpdateListener);
    this.companyBrandingListener = () => this.loadCompanyBranding();
    window.addEventListener("company-branding-updated", this.companyBrandingListener);
    await this.loadNotifications();
    this.notificationsRefreshTimer = window.setInterval(() => {
      this.loadNotifications({ silent: true });
    }, 60000);
    this.navSearchKeyListener = (event) => this.handleNavSearchShortcut(event);
    this.navSearchOutsideListener = (event) => this.handleNavSearchOutside(event);
    document.addEventListener("keydown", this.navSearchKeyListener);
    document.addEventListener("click", this.navSearchOutsideListener);
  },
  beforeUnmount() {
    if (this.profileImageListener) {
      window.removeEventListener(
        "profile-image-updated",
        this.profileImageListener
      );
    }
    if (this.profileUpdateListener) {
      window.removeEventListener("profile-updated", this.profileUpdateListener);
    }
    if (this.companyBrandingListener) {
      window.removeEventListener(
        "company-branding-updated",
        this.companyBrandingListener
      );
    }
    if (this.notificationsRefreshTimer) {
      window.clearInterval(this.notificationsRefreshTimer);
    }
    document.removeEventListener("mouseover", this.handleMouseover);
    if (this.navSearchKeyListener) document.removeEventListener("keydown", this.navSearchKeyListener);
    if (this.navSearchOutsideListener) document.removeEventListener("click", this.navSearchOutsideListener);
    if (this.navSearchTimer) window.clearTimeout(this.navSearchTimer);
  },
  watch: {
    $route() {
      this.closeMobileSidebar();
      this.closeNavSearch();
    },
    navSearchQuery(value) {
      this.navSearchActiveIndex = 0;
      if (this.navSearchTimer) window.clearTimeout(this.navSearchTimer);
      if (this.normalizeSearch(value).length < 2) return;
      this.navSearchTimer = window.setTimeout(() => this.loadNavEntitySearch(), 280);
    },
  },
  methods: {
    normalizeSearch(value) {
      return String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase().trim();
    },
    openNavSearch() {
      this.navSearchOpen = true;
      this.$nextTick(() => this.$refs.navSearchInput?.focus());
    },
    closeNavSearch() {
      this.navSearchOpen = false;
      this.navSearchActiveIndex = 0;
      if (window.innerWidth <= 991) this.navSearchQuery = "";
    },
    clearNavSearch() {
      this.navSearchQuery = "";
      this.navSearchActiveIndex = 0;
      this.$nextTick(() => this.$refs.navSearchInput?.focus());
    },
    moveNavSearch(direction) {
      const count = this.filteredNavPages.length;
      if (count) this.navSearchActiveIndex = (this.navSearchActiveIndex + direction + count) % count;
    },
    openActiveSearchResult() {
      const item = this.filteredNavPages[this.navSearchActiveIndex] || this.filteredNavPages[0];
      if (item) this.navigateFromSearch(item);
    },
    navigateFromSearch(item) {
      this.navSearchQuery = "";
      this.navSearchOpen = false;
      this.navSearchActiveIndex = 0;
      if (this.$route.path !== item.path) this.$router.push(item.path);
    },
    handleNavSearchShortcut(event) {
      if ((event.metaKey || event.ctrlKey) && String(event.key).toLowerCase() === "k") {
        event.preventDefault();
        this.openNavSearch();
      }
    },
    handleNavSearchOutside(event) {
      if (this.navSearchOpen && this.$refs.navSearch && !this.$refs.navSearch.contains(event.target)) this.closeNavSearch();
    },
    navEntityRecord(label, meta, path, icon, group, extra = "") {
      const cleanLabel = String(label || "").trim();
      if (!cleanLabel) return null;
      return {
        label: cleanLabel,
        group: [group, meta].filter(Boolean).join(" · "),
        path,
        icon,
        roles: [],
        searchText: this.normalizeSearch(`${cleanLabel} ${meta || ""} ${extra || ""}`),
      };
    },
    async loadNavEntitySearch() {
      const role = String(this.currentUser?.role || "").toLowerCase();
      if (role === "dev" || (this.navEntityItems.length && Date.now() - this.navEntityLoadedAt < 120000)) return;
      this.navEntityLoading = true;
      try {
        const results = await Promise.allSettled([
          getProductsData(), getServicesData(), getClientsData(), getDevisData(), getFacturesData(), getBonsData(),
        ]);
        const list = (index) => results[index]?.status === "fulfilled" && Array.isArray(results[index].value)
          ? results[index].value : [];
        const items = [];
        list(0).forEach((row) => items.push(this.navEntityRecord(
          row.nom || row.nom_produit,
          row.referance_produit || row.reference_produit,
          "/produits/list-produits", "box", this.$t("navSearch.product"),
          `${row.description || ""} ${row.code_barre || ""}`
        )));
        list(1).forEach((row) => items.push(this.navEntityRecord(
          row.nom || row.nom_service,
          row.reference_service,
          "/services", "briefcase", this.$t("navSearch.service"), row.description
        )));
        list(2).forEach((row) => items.push(this.navEntityRecord(
          row.nom_societe || [row.nom, row.prenom].filter(Boolean).join(" ") || row.email,
          row.reference || row.referance || row.email,
          "/tiers", "user", this.$t("navSearch.client"), `${row.telephone || ""} ${row.cin || ""}`
        )));
        list(3).forEach((row) => items.push(this.navEntityRecord(
          row.numero_devis,
          row.client_name || row.commande?.client?.nom_societe,
          "/devis", "clipboard", this.$t("navSearch.quote"), `${row.statut || ""} ${row.commande?.client?.email || ""}`
        )));
        list(4).forEach((row) => items.push(this.navEntityRecord(
          row.numero_facture,
          row.client_name || row.commande?.client?.nom_societe,
          "/factures", "file-text", this.$t("navSearch.invoice"), `${row.statut_paiement || ""} ${row.devis?.numero_devis || ""}`
        )));
        list(5).forEach((row) => items.push(this.navEntityRecord(
          row.numero_bon,
          row.facture?.numero_facture,
          "/bonsLivraison", "truck", this.$t("navSearch.delivery"), `${row.transporteur || ""} ${row.numero_suivi || ""}`
        )));
        this.navEntityItems = items.filter(Boolean);
        this.navEntityLoadedAt = Date.now();
      } finally {
        this.navEntityLoading = false;
      }
    },
    changeLanguage(locale) {
      if (locale === this.currentLocale) return;
      this.currentLocale = locale;
      setAppLocale(locale);
      this.appName = this.buildAppTitle(this.brandingCompanyName);
      this.setDocumentTitle(this.appName);
      // Reload once so every routed page, async table and hidden modal starts
      // with the selected locale. This also clears stale HMR-rendered labels.
      window.setTimeout(() => window.location.reload(), 80);
    },
    buildAppTitle(companyName = "") {
      const localizedName = this.$t("app.name");
      return companyName ? `${localizedName} | ${companyName}` : localizedName;
    },
    setDocumentTitle(title) {
      if (typeof document !== "undefined") {
        document.title = title || DEFAULT_APP_NAME;
      }
    },
    setFavicon(href) {
      if (typeof document === "undefined") return;

      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }

      link.href = href || this.defaultFaviconUrl;
    },
    normalizeStorageImageUrl(path) {
      if (!path) return null;
      if (/^https?:\/\//i.test(path) || /^data:image\//i.test(path)) return path;
      if (path.startsWith("/storage/") || path.startsWith("storage/")) {
        return this.normalizeImageUrl(path.startsWith("/") ? path : `/${path}`);
      }
      return this.normalizeImageUrl(`/storage/${path}`);
    },
    useDefaultLogo(type) {
      if (type === "small") {
        this.logoSmallUrl = this.defaultLogoSmallUrl;
      } else if (type === "white") {
        this.logoWhiteUrl = this.defaultLogoWhiteUrl;
      } else {
        this.logoUrl = this.defaultLogoUrl;
      }
    },
    async loadCompanyBranding() {
      try {
        const response = await getCompanyData();
        const company = response?.data || {};
        const societeName = String(company.societe_nom || "").trim();
        this.brandingCompanyName = societeName;
        const appTitle = this.buildAppTitle(societeName);

        this.appName = appTitle;
        this.setDocumentTitle(appTitle);

        const logo = this.normalizeStorageImageUrl(company.logo);
        const logoSmall = this.normalizeStorageImageUrl(company.logo_small);

        this.logoUrl = logo || this.defaultLogoUrl;
        this.logoWhiteUrl = logo || this.defaultLogoWhiteUrl;
        this.logoSmallUrl = logoSmall || this.defaultLogoSmallUrl;
        this.setFavicon(logoSmall || logo || this.defaultFaviconUrl);
      } catch (_) {
        this.appName = this.buildAppTitle();
        this.brandingCompanyName = "";
        this.logoUrl = this.defaultLogoUrl;
        this.logoWhiteUrl = this.defaultLogoWhiteUrl;
        this.logoSmallUrl = this.defaultLogoSmallUrl;
        this.setDocumentTitle(this.appName);
        this.setFavicon(this.defaultFaviconUrl);
      }
    },
    normalizeImageUrl(path) {
      if (!path) return null;
      if (/^https?:\/\//i.test(path)) return path;
      const baseUrl = process.env.VUE_APP_URL || window.location.origin;
      const sanitizedBase = baseUrl.endsWith("/")
        ? baseUrl.slice(0, -1)
        : baseUrl;
      const sanitizedPath = path.startsWith("/")
        ? path
        : `/${path}`;
      return `${sanitizedBase}${sanitizedPath}`;
    },
    async loadUserProfileImage() {
      try {
        if (!this.currentUser?.user_id) return;

        // The header only needs the authenticated profile. Loading the full
        // employee list fails for restricted accounts and may not return an array.
        const user = await getProfile();
        if (user?.profile_image) {
          this.userProfileImage = this.normalizeImageUrl(
            `/storage/${user.profile_image}`
          );
        } else {
          this.userProfileImage = null;
        }
      } catch (error) {
        console.error("Error loading profile image in header:", error);
        // Profile images are optional; keep the header usable if this request fails.
        this.userProfileImage = null;
      }
    },
    handleProfileImageUpdate(event) {
      const detail = event?.detail || {};
      const imageUrl =
        this.normalizeImageUrl(detail.imageUrl) ||
        this.normalizeImageUrl(
          detail.profileImagePath ? `/storage/${detail.profileImagePath}` : ""
        );
      this.userProfileImage = imageUrl || null;
    },
    handleProfileUpdate() {
      this.currentUser = getCurrentUser();
    },
    async loadNotifications(options = {}) {
      if (!this.currentUser) return;

      this.notificationsLoading = !options.silent;
      try {
        const response = await getNotifications(10);
        const data = response?.data || {};
        this.notifications = Array.isArray(data.items) ? data.items : [];
        this.notificationUnreadCount = Number(data.unread_count) || 0;
      } catch (error) {
        if (!options.silent) {
          console.error("Error loading notifications:", error);
        }
      } finally {
        this.notificationsLoading = false;
      }
    },
    async openNotification(notification) {
      if (!notification?.id || notification.is_read) return;

      try {
        await markNotificationRead(notification.id);
        notification.is_read = true;
        this.notificationUnreadCount = Math.max(0, this.notificationUnreadCount - 1);
      } catch (error) {
        console.error("Error marking notification as read:", error);
      }
    },
    async markNotificationsRead() {
      if (!this.notifications.length) return;

      try {
        await markAllNotificationsRead();
        this.notifications = this.notifications.map((notification) => ({
          ...notification,
          is_read: true,
        }));
        this.notificationUnreadCount = 0;
      } catch (error) {
        toast.error("Erreur lors de la mise à jour des notifications");
      }
    },
    async clearNotifications() {
      if (!this.notifications.length) return;

      try {
        await dismissAllNotifications();
        this.notifications = [];
        this.notificationUnreadCount = 0;
      } catch (error) {
        toast.error("Erreur lors de la suppression des notifications");
      }
    },
    notificationIcon(type) {
      if (type === "stock_low") return "box";
      if (type === "facture_unpaid") return "file-text";
      return "bell";
    },
    notificationSeverityClass(severity) {
      if (severity === "danger") return "notification-icon-danger";
      if (severity === "warning") return "notification-icon-warning";
      return "notification-icon-info";
    },
    formatNotificationTime(value) {
      if (!value) return "";

      const date = new Date(value);
      const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
      if (!Number.isFinite(seconds) || seconds < 0) return "";
      if (seconds < 60) return "À l'instant";
      const minutes = Math.floor(seconds / 60);
      if (minutes < 60) return `Il y a ${minutes} min`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `Il y a ${hours} h`;
      const days = Math.floor(hours / 24);
      if (days < 7) return `Il y a ${days} j`;

      return date.toLocaleDateString("fr-FR");
    },

    toggleSidebar1() {
      const body = document.body;
      const willOpen = !body.classList.contains("slide-nav");
      if (willOpen) {
        body.classList.add("slide-nav", "menu-opened");
      } else {
        body.classList.remove("slide-nav", "menu-opened");
      }
    },
    toggleSidebar() {
      const body = document.body;
      const willEnableMini = !body.classList.contains("mini-sidebar");
      body.classList.toggle("mini-sidebar");
      if (willEnableMini) {
        body.classList.remove("expand-menu");
        this.slideUpSubmenu();
        window.dispatchEvent(new CustomEvent("collapse-side-menus"));
      }
    },
    initFullScreen() {
      document.body.classList.toggle("fullscreen-enable");
      if (
        !document.fullscreenElement &&
        /* alternative standard method */
        !document.mozFullScreenElement &&
        !document.webkitFullscreenElement
      ) {
        // current working methods
        if (document.documentElement.requestFullscreen) {
          document.documentElement.requestFullscreen();
        } else if (document.documentElement.mozRequestFullScreen) {
          document.documentElement.mozRequestFullScreen();
        } else if (document.documentElement.webkitRequestFullscreen) {
          document.documentElement.webkitRequestFullscreen(
            Element.ALLOW_KEYBOARD_INPUT
          );
        }
      } else {
        if (document.cancelFullScreen) {
          document.cancelFullScreen();
        } else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen();
        } else if (document.webkitCancelFullScreen) {
          document.webkitCancelFullScreen();
        }
      }
    },

    closeMobileSidebar() {
      const body = document.body;
      if (window.innerWidth <= 1024) {
        body.classList.remove("slide-nav");
        body.classList.remove("menu-opened");
      }
    },

    initMouseoverListener() {
      document.addEventListener("mouseover", this.handleMouseover);
    },
    handleMouseover(e) {
      e.stopPropagation();

      const body = document.body;
      const toggleBtn = document.getElementById("toggle_btn");

      if (
        body.classList.contains("mini-sidebar") &&
        this.isElementVisible(toggleBtn)
      ) {
        const target = e.target.closest(".sidebar, .header-left");

        if (target) {
          body.classList.add("expand-menu");
          this.slideDownSubmenu();
        } else {
          body.classList.remove("expand-menu");
          this.slideUpSubmenu();
        }

        e.preventDefault();
      }
    },
    isElementVisible(element) {
      return element.offsetWidth > 0 || element.offsetHeight > 0;
    },
    slideDownSubmenu() {
      const subdropPlusUl = document.getElementsByClassName("subdrop");
      for (let i = 0; i < subdropPlusUl.length; i++) {
        const submenu = subdropPlusUl[i].nextElementSibling;
        if (submenu && submenu.tagName.toLowerCase() === "ul") {
          submenu.style.display = "block";
        }
      }
    },
    slideUpSubmenu() {
      const subdropPlusUl = document.getElementsByClassName("subdrop");
      for (let i = 0; i < subdropPlusUl.length; i++) {
        const submenu = subdropPlusUl[i].nextElementSibling;
        if (submenu && submenu.tagName.toLowerCase() === "ul") {
          submenu.style.display = "none";
        }
      }
    },
    async logoutUser() {
      await logout();
      this.$router.push({ path: "/" });
    },

    // Method to refresh profile image when updated from profile page
    refreshProfileImage() {
      this.loadUserProfileImage();
    },
  },
};
</script>

<style scoped>
.nav-global-search {
  left: 50%;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: min(390px, 31vw);
  z-index: 1045;
}

.nav-search-control {
  align-items: center;
  background: #f5f8fc;
  border: 1px solid #e1e8f0;
  border-radius: 11px;
  display: flex;
  height: 42px;
  padding: 0 9px 0 12px;
  transition: border-color .18s ease, box-shadow .18s ease, background .18s ease;
}

.nav-global-search.open .nav-search-control {
  background: #fff;
  border-color: #91bde8;
  box-shadow: 0 0 0 3px rgba(37, 124, 212, .10);
}

.nav-search-icon { color: #71849a; flex: 0 0 auto; }
.nav-search-control input {
  background: transparent;
  border: 0;
  box-shadow: none !important;
  color: #17324d;
  flex: 1;
  font-size: 13px;
  height: 100%;
  min-width: 0;
  outline: 0;
  padding: 0 9px;
}
.nav-search-control input::-webkit-search-cancel-button { display: none; }
.nav-search-control input::placeholder { color: #92a1b2; }
.nav-search-clear {
  align-items: center; background: transparent; border: 0; border-radius: 6px; color: #71849a;
  display: flex; height: 26px; justify-content: center; padding: 0; width: 26px;
}
.nav-search-clear:hover { background: #edf2f7; color: #17324d; }
.nav-search-shortcut {
  background: #fff; border: 1px solid #dce4ed; border-radius: 6px; box-shadow: none; color: #8292a6;
  font-family: inherit; font-size: 10px; line-height: 1; padding: 5px 6px; white-space: nowrap;
}
.nav-search-toggle { display: none; }
.nav-search-results {
  background: #fff; border: 1px solid #e5ebf2; border-radius: 13px; box-shadow: 0 18px 48px rgba(15, 42, 68, .18);
  left: 0; margin-top: 7px; overflow: hidden; padding: 7px; position: absolute; right: 0;
}
.nav-search-results-title {
  color: #94a3b8; font-size: 10px; font-weight: 750; letter-spacing: .05em; padding: 5px 8px 7px; text-transform: uppercase;
}
.nav-search-result {
  align-items: center; background: transparent; border: 0; border-radius: 9px; color: #52677d;
  display: flex; gap: 10px; padding: 8px; text-align: start; width: 100%;
}
.nav-search-result.active { background: #edf6ff; color: #1769aa; }
.nav-search-result-icon {
  align-items: center; background: #f1f5f9; border-radius: 8px; color: #47627d; display: flex;
  flex: 0 0 auto; height: 34px; justify-content: center; width: 34px;
}
.nav-search-result.active .nav-search-result-icon { background: #dbeeff; color: #1774bd; }
.nav-search-result-copy { display: block; flex: 1; min-width: 0; }
.nav-search-result-copy strong { color: #233d57; display: block; font-size: 12px; font-weight: 700; }
.nav-search-result-copy small { color: #91a0b1; display: block; font-size: 10px; margin-top: 1px; }
.nav-search-empty { align-items: center; color: #91a0b1; display: flex; flex-direction: column; gap: 7px; padding: 25px 12px; font-size: 12px; }
.nav-search-loading { align-items: center; border-top: 1px solid #edf1f5; color: #71849a; display: flex; font-size: 11px; gap: 8px; margin: 5px 4px 0; padding: 10px 8px 5px; }
.nav-search-loading .spinner-border { height: 14px; width: 14px; }
.nav-search-fade-enter-active, .nav-search-fade-leave-active { transition: opacity .14s ease, transform .14s ease; }
.nav-search-fade-enter-from, .nav-search-fade-leave-to { opacity: 0; transform: translateY(-5px); }

html[dir="rtl"] .nav-search-control { padding: 0 12px 0 9px; }
html[dir="rtl"] .nav-search-results { direction: rtl; }

.user-avatar-img {
  width: 46px;
  height: 46px;
  border-radius: 50%;
  object-fit: cover;
  background-color: #f5f7fb;
  border: 2px solid #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.language-trigger {
  align-items: center;
  display: flex !important;
  font-size: 12px !important;
  font-weight: 700;
  gap: 5px;
  direction: ltr;
  white-space: nowrap;
}

.user-menu {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 0;
  box-shadow: none;
  gap: 6px;
  height: 50px;
  padding: 4px 6px;
}

.user-menu .nav-item-box > .nav-link,
.user-menu .nav-item-box > a {
  align-items: center;
  border: 0;
  border-radius: 10px;
  display: inline-flex;
  height: 40px;
  justify-content: center;
  min-width: 40px;
  padding: 0 9px !important;
}

.user-menu .nav-item-box > .nav-link:hover,
.user-menu .nav-item-box > a:hover,
.user-menu .nav-item-box > .nav-link.show {
  background: #f2f7fc !important;
}

.user-menu .main-drop {
  border-inline-start: 0;
  margin-inline-start: 2px;
  padding-inline-start: 7px;
}

.user-menu .main-drop .userset {
  border-radius: 10px;
  min-height: 40px;
  padding: 3px 7px !important;
}

.user-menu .main-drop .userset:hover,
.user-menu .main-drop .userset.show {
  background: #f4f7fb;
}

.user-menu .user-info {
  align-items: center;
  display: flex;
  gap: 8px;
}

.user-menu .user-letter,
.user-menu .user-letter img {
  height: 34px !important;
  width: 34px !important;
}

.user-menu .user-letter img {
  border: 0;
  box-shadow: none;
}

.user-menu .user-detail {
  line-height: 1.2;
  min-width: 92px;
}

.user-menu .user-name {
  color: #17324d;
  display: block;
  font-size: 12px;
  font-weight: 700;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-menu .user-role {
  color: #8696a8;
  display: block;
  font-size: 10px;
  margin-top: 2px;
}

html[dir="rtl"] .user-menu .user-info,
html[dir="rtl"] .language-dropdown,
html[dir="rtl"] .notifications,
html[dir="rtl"] .menu-drop-user {
  direction: rtl;
}

.language-dropdown {
  border: 0;
  border-radius: 12px;
  box-shadow: 0 12px 35px rgba(15, 23, 42, .16);
  min-width: 190px;
  padding: 7px;
  margin-top: 9px !important;
}

.language-dropdown .dropdown-item {
  align-items: center;
  border: 0;
  border-radius: 8px;
  display: flex;
  gap: 9px;
  justify-content: flex-start;
  padding: 9px 10px;
}

.language-dropdown .dropdown-item.active {
  background: #edf6ff;
  color: #2563eb;
}

.language-dropdown .dropdown-item svg:last-child {
  margin-inline-start: auto;
}

.language-code {
  background: #f1f5f9;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 750;
  padding: 4px 5px;
}

.notifications {
  border: 0;
  border-radius: 16px;
  box-shadow: 0 18px 50px rgba(15, 23, 42, 0.18);
  margin-top: 10px !important;
  overflow: hidden;
  padding: 0;
  width: 390px;
}

.nav-item.dropdown > .notifications {
  inset: 100% 0 auto auto !important;
  margin-top: 10px !important;
  transform: none !important;
}

.notifications .topnav-dropdown-header {
  align-items: center;
  background: #fff;
  border-bottom: 1px solid #edf1f5;
  display: flex;
  justify-content: space-between;
  height: auto !important;
  line-height: normal !important;
  padding: 17px 18px;
}

.notification-title {
  color: #102a43;
  display: block;
  font-size: 16px;
  font-weight: 750;
}

.notification-count {
  color: #8292a6;
  display: block;
  font-size: 11px;
  margin-top: 2px;
}

.notification-clear-btn {
  align-items: center;
  background: #f4f6f9;
  border: 0;
  border-radius: 9px;
  color: #64748b;
  display: inline-flex;
  height: 32px;
  justify-content: center;
  transition: .2s ease;
  width: 32px;
}

.notification-clear-btn:hover {
  background: #fee2e2;
  color: #dc2626;
}

.notifications .noti-content {
  height: auto !important;
  max-height: min(430px, calc(100vh - 190px));
  min-height: 0;
  overflow-y: auto;
  padding: 7px;
  position: relative;
  width: 100% !important;
}

.notification-list {
  margin: 0;
  padding: 0;
}

.notification-message {
  border: 0 !important;
  border-radius: 11px;
  margin-bottom: 3px;
  overflow: hidden;
  position: relative;
}

.notification-message > a {
  display: block;
  padding: 12px !important;
  position: relative;
  transition: background-color .18s ease;
}

.notification-message > a:hover {
  background: #f6f8fb;
}

.notification-message.unread > a {
  background: #f0f7ff;
}

.notification-message.unread > a:hover {
  background: #e7f2ff;
}

.notification-unread-dot {
  background: #ff9f43;
  border: 2px solid #fff;
  border-radius: 50%;
  height: 9px;
  position: absolute;
  right: 11px;
  top: 12px;
  width: 9px;
}

.notification-icon {
  align-items: center;
  border-radius: 11px;
  display: inline-flex;
  height: 40px;
  justify-content: center;
  margin-right: 11px;
  width: 40px;
}

.notification-icon-danger {
  background: #fee2e2;
  color: #dc2626;
}

.notification-icon-warning {
  background: #fff3d6;
  color: #d97706;
}

.notification-icon-info {
  background: #dbeafe;
  color: #2563eb;
}

.noti-details,
.notification-description,
.noti-time {
  margin: 0;
}

.noti-title {
  color: #243b53;
  display: block;
  font-size: 13px;
  font-weight: 700;
  padding-right: 14px;
}

.notification-description {
  color: #62748a;
  font-size: 12px;
  line-height: 1.45;
  margin-top: 3px;
}

.notification-time {
  color: #94a3b8;
  font-size: 11px;
}

.notification-empty {
  align-items: center;
  color: #8292a6;
  display: flex;
  gap: 9px;
  justify-content: center;
  min-height: 110px;
  padding: 18px;
}

.notification-empty-state {
  flex-direction: column;
  min-height: 210px;
}

.notification-empty-state strong {
  color: #334e68;
  font-size: 14px;
}

.notification-empty-state > span:last-child {
  font-size: 12px;
}

.notification-empty-icon {
  align-items: center;
  background: #f1f5f9;
  border-radius: 50%;
  color: #7890a8;
  display: flex;
  height: 54px;
  justify-content: center;
  margin-bottom: 3px;
  width: 54px;
}

.notifications .topnav-dropdown-footer {
  background: #fff;
  border-top: 1px solid #edf1f5;
  padding: 10px;
  height: auto !important;
  line-height: normal !important;
}

.notifications .topnav-dropdown-footer button {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 9px;
  color: #2563eb;
  display: flex;
  font-size: 12px;
  font-weight: 650;
  gap: 7px;
  justify-content: center;
  padding: 9px;
  width: 100%;
}

.notifications .topnav-dropdown-footer button:hover:not(:disabled) {
  background: #eff6ff;
}

.notifications .topnav-dropdown-footer button:disabled {
  color: #a8b3c2;
  cursor: default;
}

@media (max-width: 576px) {
  .notifications {
    max-width: calc(100vw - 24px);
    width: 360px;
  }

  .user-avatar-img {
    width: 40px;
    height: 40px;
  }
}

@media (max-width: 1100px) {
  .nav-global-search { width: min(300px, 28vw); }
  .nav-search-shortcut { display: none; }
}

@media (max-width: 991px) {
  .nav-global-search {
    left: auto; margin-inline-start: auto; position: static; top: auto; transform: none; width: 40px; z-index: 1100;
  }
  .nav-search-toggle {
    align-items: center; background: #f5f8fc; border: 0; border-radius: 9px; color: #17324d;
    display: flex; height: 40px; justify-content: center; width: 40px;
  }
  .nav-search-control { display: none; }
  .nav-global-search.open {
    inset: 7px 10px auto 10px; margin: 0; position: fixed; width: auto;
  }
  .nav-global-search.open .nav-search-toggle { display: none; }
  .nav-global-search.open .nav-search-control { display: flex; height: 46px; }
  .nav-search-results { max-height: calc(100vh - 75px); overflow-y: auto; }
}

/* Compact mobile header: menu | centred logo | search + account */
@media (max-width: 767px) {
  .header {
    background: #fff !important;
    border-bottom: 1px solid #e8edf3 !important;
    box-shadow: 0 2px 8px rgba(15, 42, 67, .04) !important;
  }

  .header {
    height: 64px !important;
    min-height: 64px !important;
  }

  .header .header-left {
    align-items: center !important;
    display: flex !important;
    height: 64px !important;
    inset: 0 !important;
    justify-content: center !important;
    padding: 0 108px 0 56px !important;
    position: absolute !important;
    width: 100% !important;
  }

  .header .header-left .logo-normal {
    align-items: center;
    display: flex !important;
    height: 64px;
    justify-content: center;
    margin: 0 !important;
    width: auto !important;
  }

  .header .header-left .logo-normal img {
    height: auto !important;
    max-height: 42px !important;
    max-width: 136px !important;
    object-fit: contain;
    width: auto !important;
  }

  .header .header-left .logo-white,
  .header .header-left .logo-small,
  .header #toggle_btn,
  .header .user-menu {
    display: none !important;
  }

  .header .mobile_btn {
    align-items: center;
    background: #fff7ef;
    border-radius: 11px;
    display: flex !important;
    height: 40px !important;
    inset-inline-start: 8px !important;
    justify-content: center;
    left: auto !important;
    padding: 0 !important;
    right: auto !important;
    top: 12px !important;
    width: 40px !important;
    z-index: 1202;
    opacity: 1 !important;
    visibility: visible !important;
  }

  .header .mobile_btn svg {
    color: #ff9f43 !important;
    display: block !important;
    height: 23px !important;
    stroke: currentColor !important;
    width: 23px !important;
  }

  .header .nav-global-search {
    inset-inline-end: 54px !important;
    left: auto !important;
    margin: 0 !important;
    position: absolute !important;
    right: auto !important;
    top: 12px !important;
    width: 40px !important;
    z-index: 1202;
  }

  .header .nav-search-toggle,
  .header .mobile-more-button {
    align-items: center !important;
    background: #f4f7fb !important;
    border: 1px solid #e7edf5 !important;
    border-radius: 11px !important;
    color: #17324d !important;
    display: flex !important;
    height: 40px !important;
    justify-content: center !important;
    margin: 0 !important;
    padding: 0 !important;
    width: 40px !important;
  }

  /* Use the template responsive-search treatment: a clean icon without an extra floating tile. */
  .header .nav-search-toggle.responsive-search {
    background: transparent !important;
    border-color: transparent !important;
    color: #17324d !important;
    font-size: 18px !important;
    line-height: 40px !important;
  }

  .header .nav-search-toggle.responsive-search:hover,
  .header .nav-search-toggle.responsive-search:focus {
    background: #f4f7fb !important;
    border-color: #e7edf5 !important;
  }

  .header .nav-search-toggle svg,
  .header .mobile-more-button svg {
    height: 19px !important;
    stroke: currentColor !important;
    width: 19px !important;
  }

  .header .mobile-user-menu {
    display: block !important;
    height: 40px !important;
    inset-inline-end: 8px !important;
    left: auto !important;
    position: absolute !important;
    right: auto !important;
    top: 12px !important;
    width: 40px !important;
    z-index: 1203;
  }

  .header .mobile-more-button::after { display: none !important; }

  .header .mobile-quick-menu {
    border: 1px solid #e8edf4;
    border-radius: 14px;
    box-shadow: 0 16px 45px rgba(15, 42, 67, .16);
    margin-top: 9px !important;
    min-width: 232px;
    overflow: hidden;
    padding: 10px;
  }

  .mobile-quick-profile {
    align-items: center;
    border-bottom: 1px solid #edf1f5;
    display: flex;
    gap: 24px;
    margin-bottom: 8px;
    padding: 4px 4px 11px;
  }

  .mobile-quick-profile img { height: 36px !important; width: 36px !important; }
  .mobile-quick-profile span { display: flex; flex-direction: column; min-width: 0; }
  .mobile-quick-profile strong { color: #17324d; font-size: 13px; line-height: normal; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .mobile-quick-profile small { color: #8492a6; font-size: 11px; line-height: normal;}

  .mobile-language-list {
    background: #f5f7fa;
    border-radius: 9px;
    display: grid;
    gap: 4px;
    grid-template-columns: repeat(3, 1fr);
    margin-bottom: 8px;
    padding: 4px;
  }

  .mobile-language-list button {
    background: transparent;
    border: 0;
    border-radius: 7px;
    color: #66778a;
    font-size: 11px;
    font-weight: 700;
    padding: 7px 4px;
  }

  .mobile-language-list button.active { background: #fff; color: #ff9f43; box-shadow: 0 2px 8px rgba(15, 42, 67, .08); }
  .mobile-quick-menu .dropdown-item { align-items: center; border-radius: 8px; display: flex; font-size: 13px; gap: 9px; padding: 9px 10px; }
  .mobile-quick-menu .mobile-logout { color: #dc2626; }

  .header .nav-global-search.open {
    inset: 7px 10px auto 10px !important;
    position: fixed !important;
    width: auto !important;
    z-index: 1300;
  }

  /* Explicit physical positions prevent legacy header rules from mixing with logical RTL offsets. */
  :global(html[dir="ltr"]) .header .mobile_btn {
    left: 8px !important;
    right: auto !important;
  }

  :global(html[dir="ltr"]) .header .mobile-user-menu {
    left: auto !important;
    right: 8px !important;
  }

  :global(html[dir="ltr"]) .header .nav-global-search:not(.open) {
    left: auto !important;
    right: 56px !important;
    transform: none !important;
  }

  :global(html[dir="rtl"]) .header .mobile_btn {
    left: auto !important;
    right: 8px !important;
  }

  :global(html[dir="rtl"]) .header .mobile-user-menu {
    left: 8px !important;
    right: auto !important;
  }

  :global(html[dir="rtl"]) .header .nav-global-search:not(.open) {
    left: 56px !important;
    right: auto !important;
    transform: none !important;
  }

  /* Final mobile layout follows the original template header proportions. */
  .header {
    height: 60px !important;
    min-height: 60px !important;
  }

  .header .header-left {
    height: 60px !important;
    left: 0 !important;
    padding: 0 !important;
    right: 0 !important;
    width: 100% !important;
    z-index: 10 !important;
  }

  .header .header-left .logo-normal {
    height: 60px !important;
    text-align: center !important;
    width: 100% !important;
  }

  .header .header-left .logo-normal img {
    max-height: 44px !important;
    max-width: 128px !important;
  }

  .header .mobile_btn {
    background: transparent !important;
    border: 0 !important;
    border-radius: 0 !important;
    height: 60px !important;
    line-height: 60px !important;
    padding: 0 12px !important;
    top: 0 !important;
    width: 48px !important;
    z-index: 30 !important;
  }

  .header .mobile_btn svg {
    color: #ff9f43 !important;
    height: 24px !important;
    width: 24px !important;
  }

  .header .mobile-user-menu {
    display: block !important;
    height: 60px !important;
    line-height: 60px !important;
    padding: 0 !important;
    top: 0 !important;
    width: 48px !important;
    z-index: 31 !important;
  }

  .header .mobile-more-button,
  .header .nav-search-toggle.responsive-search {
    background: transparent !important;
    border: 0 !important;
    border-radius: 0 !important;
    color: #ff9f43 !important;
    height: 60px !important;
    line-height: 60px !important;
    width: 48px !important;
  }

  .header .mobile-more-button svg {
    color: #ff9f43 !important;
    height: 22px !important;
    width: 22px !important;
  }

  .header .nav-global-search {
    height: 60px !important;
    top: 0 !important;
    width: 48px !important;
    z-index: 30 !important;
  }

  .header .nav-search-toggle.responsive-search i {
    color: #ff9f43 !important;
    font-size: 19px !important;
  }

  :global(html[dir="ltr"]) .header .mobile_btn { left: 6px !important; right: auto !important; }
  :global(html[dir="ltr"]) .header .mobile-user-menu { left: auto !important; right: 6px !important; }
  :global(html[dir="ltr"]) .header .nav-global-search:not(.open) { left: auto !important; right: 62px !important; }
  :global(html[dir="rtl"]) .header .mobile_btn { left: auto !important; right: 6px !important; }
  :global(html[dir="rtl"]) .header .mobile-user-menu { left: 6px !important; right: auto !important; }
  :global(html[dir="rtl"]) .header .nav-global-search:not(.open) { left: 62px !important; right: auto !important; }

  .header .nav-global-search.open {
    height: auto !important;
    inset: 7px 10px auto 10px !important;
    width: auto !important;
    z-index: 1300 !important;
  }
}
</style>

<!-- Mobile header positioning must be global because the template and App.vue also style these root elements. -->
<style>
@media (max-width: 767px) {
  .header #mobile_btn.mobile_btn {
    align-items: center !important;
    background: #f7f8fa !important;
    border: 0 !important;
    display: flex !important;
    border-radius: 8px !important;
    height: 40px !important;
    justify-content: center !important;
    opacity: 1 !important;
    padding: 0 !important;
    position: absolute !important;
    top: 10px !important;
    visibility: visible !important;
    width: 40px !important;
    z-index: 1205 !important;
  }

  .header #mobile_btn.mobile_btn:hover,
  .header #mobile_btn.mobile_btn:focus {

    color: #ff9f43 !important;
  }

  .header #mobile_btn.mobile_btn svg {
    color: #ff9f43 !important;
    display: block !important;
    height: 22px !important;
    stroke-width: 2 !important;
    stroke: currentColor !important;
    width: 22px !important;
  }

  .header .mobile-user-menu {
    align-items: center !important;
    display: flex !important;
    float: none !important;
    height: 60px !important;
    justify-content: center !important;
    padding: 0 !important;
    position: absolute !important;
    top: 0 !important;
    width: 48px !important;
    z-index: 1206 !important;
  }

  .header .mobile-user-menu > .mobile-more-button {
    align-items: center !important;
    border: 0 !important;
    color: #ff9f43 !important;
    display: flex !important;
    border-radius: 8px !important;

    justify-content: center !important;
    padding: 0 !important;
    width: 40px !important;
  }

  .header .mobile-user-menu > .mobile-more-button:hover,
  .header .mobile-user-menu > .mobile-more-button:focus {

    color: #ff9f43 !important;
  }

  .header .mobile-user-menu > .mobile-more-button svg {
    height: 20px !important;
    stroke-width: 2.4 !important;
    width: 20px !important;
  }

  .header .nav-global-search:not(.open) {
    display: block !important;
    height: 60px !important;
    margin: 0 !important;
    position: absolute !important;
    top: 0 !important;
    transform: none !important;
    width: 48px !important;
    z-index: 1205 !important;
  }

  .header .nav-global-search:not(.open) .nav-search-toggle {
    align-items: center !important;
    display: flex !important;
    background: #f7f8fa !important;
    border: 0 !important;
    border-radius: 8px !important;
    height: 40px !important;
    justify-content: center !important;
    padding: 0 !important;
    margin: 10px 4px !important;
    width: 40px !important;
  }

  .header .nav-global-search:not(.open) .nav-search-toggle:hover,
  .header .nav-global-search:not(.open) .nav-search-toggle:focus {
    color: #ff9f43 !important;
  }

  .header .nav-global-search:not(.open) .nav-search-toggle i {
    color: #ff9f43 !important;
    font-size: 18px !important;
  }

  .header .header-left .logo-normal img {
    filter: none !important;
    max-height: 42px !important;
    max-width: 124px !important;
  }

  html:not([dir="rtl"]) .header #mobile_btn.mobile_btn { left: 10px !important; right: auto !important; }
  html:not([dir="rtl"]) .header .mobile-user-menu { left: auto !important; right: 6px !important; }
  html:not([dir="rtl"]) .header .nav-global-search:not(.open) { left: auto !important; right: 62px !important; }

  html[dir="rtl"] .header #mobile_btn.mobile_btn { left: auto !important; right: 10px !important; }
  html[dir="rtl"] .header .mobile-user-menu { left: 6px !important; right: auto !important; }
  html[dir="rtl"] .header .nav-global-search:not(.open) { left: 62px !important; right: auto !important; }
}
</style>
